<template>
  <div>
    <b-form
      inline
      class="mb-2"
    >
      <b-button
        v-if="isPermitted('messageManagement')"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        class="d-inline-block mr-1 mb-1"
        @click="createNew"
      >
        {{ $t('Add') }}
      </b-button>

      <b-form-input
        v-model="searchTerm"
        :placeholder=" $t('message.SearchPlaceholder')"
        type="text"
        class="d-inline mb-1"
      />

      <b-dropdown
        v-if="selectedRecords.length>0"
        id="dropdown-actions"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        :text="$t('Actions')"
        variant="primary"
        class="ml-1 mb-1"
      >
        <b-dropdown-item
          variant="success"
          @click="setStatusSelectedRecords(true)"
        >
          <feather-icon
            icon="EyeIcon"
          />
          {{ $t('Mark as Read') }}
        </b-dropdown-item>
        <b-dropdown-item
          variant="warning"
          @click="setStatusSelectedRecords(false)"
        >
          <feather-icon
            icon="EyeOffIcon"
          />
          {{ $t('Mark as Unread') }}
        </b-dropdown-item>
        <b-dropdown-divider v-if="isPermitted('messageManagement')" />
        <b-dropdown-item
          v-if="isPermitted('messageManagement')"
          variant="danger"
          @click="confirmDelete"
        >
          <feather-icon
            icon="Trash2Icon"
          />
          {{ $t('Delete') }}
        </b-dropdown-item>
      </b-dropdown>

    </b-form>

    <!-- table -->
    <vue-good-table
      style-class="vgt-table striped"
      :columns="columns"
      :rows="data"
      :row-style-class="rowStyleClassFn"
      :sort-options="{
        enabled: true,
        initialSortBy: {field: 'id', type: 'desc'}
      }"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :select-options="{
        enabled: true,
        selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true, // disable the select info panel on top
      }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength,
        mode: 'records',
        perPage: 10,
        perPageDropdown: [10, 50, 100],
        dropdownAllowAll: false,
      }"
      @on-selected-rows-change="selectedRowsChanged"
    >

      <template
        slot="table-column"
        slot-scope="props"
      >
        <span>
          {{ $t(props.column.label) }}
        </span>
      </template>

      <template
        slot="table-row"
        slot-scope="props"
      >

        <!-- Column: id -->
        <span
          v-if="props.column.field === 'id'"
          class="text-nowrap"
        >
          <b-link
            @click="edit(props.row)"
          >
            <feather-icon
              v-if="isPermitted('messageManagement')"
              icon="EditIcon"
            />

          </b-link>
        </span>

        <span
          v-else-if="props.column.field === 'title'"
          class="text-nowrap"
        >
          <b-link
            @click="readMessage(props.row)"
          >
            {{ props.row.title }}
          </b-link>
        </span>
        <span
          v-else-if="props.column.field === 'icon'"
          class="text-nowrap"
        >
          <feather-icon
            :icon="props.row[props.column.field]||'CircleIcon'"
          />
        </span>

        <span
          v-else-if="props.column.field === 'createDate'"
          class="text-nowrap"
        >
          {{ formatDate(props.row.createDate) }}
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

    </vue-good-table>

    <modal-item
      item-type="message"
      :edit-item="edit_item"
      @submit="getMessages"
    />

  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  BLink, BFormInput, BForm, BButton, BDropdown, BDropdownDivider, BDropdownItem,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { VueGoodTable } from 'vue-good-table'
import { getUserData, isPermitted } from '@/auth/utils'
import ModalItem from './ModalItem.vue'

dayjs.extend(relativeTime)

export default {
  components: {
    VueGoodTable,
    BLink,
    BDropdown,
    BDropdownDivider,
    BDropdownItem,
    BFormInput,
    BForm,
    BButton,
    ModalItem,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      data: [],
      readItem: null,
      columns: [
        {
          label: '#',
          field: 'id',
          type: 'number',
          tdClass: 'text-center',
          thClass: 'text-center',
          width: '50px',
        },
        {
          label: '',
          field: 'icon',
          tdClass: 'text-center',
          thClass: 'text-center',
          width: '50px',
          sortable: false,
        },
        {
          label: 'Title',
          field: 'title',
        },
        {
          label: 'Date',
          field: 'createDate',
        },
      ],
      pageLength: 10,
      searchTerm: '',
      selectedRecords: [],
      edit_item: null,
    }
  },
  created() {
    this.userData = getUserData()
    this.getMessages()
  },
  methods: {
    rowStyleClassFn(row) {
      return row.isNew ? 'bold' : 'normal'
    },
    isPermitted(code) {
      // skip check if pdf export
      if (!this.userData) {
        return false
      }
      return isPermitted(this.userData.role, code, this.userData.userGroups)
    },
    getMessages() {
      this.edit_item = null
      this.$bvModal.hide('add-item')
      useJwt.axiosIns.get('message')
        .then(response => {
          if (response.data) {
            this.data = response.data
            this.data.forEach(element => {
              const newElement = element
              if (newElement.userGroupsJson) {
                newElement.userGroups = JSON.parse(newElement.userGroupsJson)
              }
            })
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    formatDate(date) {
      return dayjs(date).fromNow()
    },
    edit(item) {
      this.edit_item = JSON.parse(JSON.stringify(item))
      this.$bvModal.show('add-item')
    },
    readMessage(message) {
      const msg = this.data.find(m => m.id === message.id)
      msg.isNew = false
      this.$root.$emit('read_message', message)
    },
    mounted() {
      this.$root.$on('refreshMessagesTable', () => {
        this.getMessages()
      })
    },
    beforeDestroy() {
      this.$root.$off('refreshMessagesTable')
    },
    createNew() {
      this.$bvModal.show('add-item')
    },
    selectedRowsChanged(vals) {
      this.selectedRecords = vals.selectedRows.map(x => x.id)
    },

    confirmDelete() {
      this.$swal({
        title: this.$t('Are you sure?'),
        text: this.$t('You are about to delete selected record(s)...'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Yes, delete it!'),
        cancelButtonText: this.$t('Cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteSelectedRecords()
        }
      })
    },

    deleteSelectedRecords() {
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.delete(
        `message/${x}`,
      ))

      Promise.all(promises).then(() => {
        this.getMessages()
        this.$root.$emit('refresh_messages')

        // show message
        this.$swal({
          icon: 'success',
          title: this.$t('Deleted!'),
          text: this.$t('Selected record(s) has been deleted.'),
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },
    setStatusSelectedRecords(status) {
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.patch(
        `message/${status ? 'setAsRead' : 'setAsUnRead'}/${x}`,
      ))

      Promise.all(promises).then(() => {
        this.getMessages()
        this.$root.$emit('refresh_messages')
        // show message
        this.$swal({
          icon: 'success',
          title: this.$t('Success!'),
          text: this.$t(`Selected record(s) has been marked as ${status ? 'Read' : 'Unread'}.`),
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },

  },
}
</script>

<style lang="scss" >
@import '@core/scss/vue/libs/vue-good-table.scss';
.bold{
  font-weight: 600;
}
.normal{
  font-weight: inherit;
}
</style>
